// frontend/src/types/imageUpload.ts

export interface ImageFile {
  id: string;
  file?: File;
  url: string;
  caption?: string;
  isPrimary?: boolean;
  isUploading?: boolean;
  isUploaded?: boolean;
  error?: string;
  progress?: number;
  media_id?: number; // For uploaded images
  original_filename?: string;
  file_size?: number;
  width?: number;
  height?: number;
}

export interface ImageUploadProps {
  // Configuration
  multiple?: boolean;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedTypes?: string[];
  entityType?: string;
  entityId?: number | string;
  
  // Features
  allowPrimary?: boolean;
  allowCaptions?: boolean;
  allowCrop?: boolean;
  compressionQuality?: number;
  requiredDimensions?: {
    min?: { width: number; height: number };
    max?: { width: number; height: number };
  };
  
  // Initial data
  initialImages?: ImageFile[];
  
  // Styling
  className?: string;
  dropzoneClassName?: string;
  previewClassName?: string;
  
  // Callbacks
  onUpload?: (files: ImageFile[]) => Promise<void>;
  onDelete?: (imageId: string) => Promise<void>;
  onUpdateCaption?: (imageId: string, caption: string) => Promise<void>;
  onSetPrimary?: (imageId: string) => Promise<void>;
  onChange?: (images: ImageFile[]) => void;
  
  // UI Configuration
  showProgress?: boolean;
  showPreview?: boolean;
  dragDropText?: string;
  browseText?: string;
  compact?: boolean;
}

export interface UseImageUploadOptions {
  entityType?: string;
  entityId?: number | string;
  apiEndpoint?: string;
}

export interface ApiResponse {
  success: boolean;
  data?: any;
  message?: string;
}

export interface UploadedImageData {
  media_id: number;
  url: string;
  thumbnail_url?: string;
  filename: string;
  original_filename: string;
  file_size: number;
  dimensions?: {
    width: number;
    height: number;
  };
  mime_type?: string;
  entity_type?: string;
  entity_id?: number;
}

// Preset configurations for different use cases
export const UPLOAD_PRESETS = {
  USER_AVATAR: {
    multiple: false,
    maxFiles: 1,
    maxFileSize: 1 * 1024 * 1024, // 1MB
    acceptedTypes: ['image/jpeg', 'image/png'],
    allowCrop: true,
    requiredDimensions: {
      min: { width: 150, height: 150 },
      max: { width: 2000, height: 2000 }
    }
  },
  
  PROVIDER_PROFILE: {
    multiple: false,
    maxFiles: 1,
    maxFileSize: 2 * 1024 * 1024, // 2MB
    acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowCrop: true,
    requiredDimensions: {
      min: { width: 200, height: 200 }
    }
  },
  
  PROVIDER_GALLERY: {
    multiple: true,
    maxFiles: 15,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowPrimary: true,
    allowCaptions: true,
    compressionQuality: 0.85,
    requiredDimensions: {
      min: { width: 800, height: 600 }
    }
  },
  
  SERVICE_IMAGES: {
    multiple: true,
    maxFiles: 8,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowPrimary: true,
    allowCaptions: true,
    compressionQuality: 0.8,
    requiredDimensions: {
      min: { width: 1200, height: 800 }
    }
  },
  
  CATEGORY_ICON: {
    multiple: false,
    maxFiles: 1,
    maxFileSize: 500 * 1024, // 500KB
    acceptedTypes: ['image/svg+xml', 'image/png'],
    requiredDimensions: {
      min: { width: 64, height: 64 },
      max: { width: 512, height: 512 }
    }
  },
  
  BOOKING_ATTACHMENT: {
    multiple: true,
    maxFiles: 5,
    maxFileSize: 3 * 1024 * 1024, // 3MB
    acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowCaptions: true
  }
} as const;